import classNames from "classnames";
import { ReactNode, useMemo, useState } from "react";
import { GetPhoneOptionsResponse, PhoneContact } from "../../../lib/api/models";
import {
  formatInCentralTime,
  INPUT_MASKS,
  maskInput,
} from "../../../lib/formatting";
import TextButton from "../../common/buttons/TextButton";
import StyledCheckbox from "../../common/inputs/StyledCheckbox";
import Table from "../../common/tables/Table";
import { TableColumn } from "../../common/tables/Table/Table";
import styles from "./PhoneNumberTable.module.css";

const strings = {
  number: "Number",
  owner: "Owner",
  cellOwnerName: "Cell Owner Name",
  type: "Type",
  asc: "ASC",
  lastVerified: "Last Verified",
  lastCaptured: "Last Captured",
  source: "Source",
  reason: "Reason",
  edit: "Edit",
  view: "View",
  verify: "Verify",
  primary: "Primary",
  secondary: "Secondary",
  restricted: "R",
  yes: "Yes",
  no: "No",
  unknown: "Unknown",
  extension: (s: string) => `ext. ${s}`,
  restrictedBadge: "Restricted Use",
};

const renderTableHeaderCell = (
  label: string,
  onClick: () => void,
  desc?: boolean,
) => (
  <button type="button" className={styles.headerCell} onClick={onClick}>
    {label}
    <div className={styles.sortIcon}>
      <div className={styles.sortIcon}>
        {desc === undefined || !desc ? (
          <div className={styles.triangleUp} />
        ) : null}
        {desc === undefined || desc ? (
          <div className={styles.triangleDown} />
        ) : null}
      </div>
    </div>
  </button>
);

const renderAscBadge = (asc: PhoneContact["consentStatus"]) => {
  if (!asc) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.badge,
        asc === "PEC_YES"
          ? styles.success
          : asc === "PEC_NO"
            ? styles.danger
            : styles.warning,
      )}
    >
      {asc === "PEC_YES"
        ? strings.yes
        : asc === "PEC_NO"
          ? strings.no
          : strings.unknown}
    </div>
  );
};

type SortingOption =
  | "number"
  | "owner"
  | "ownerName"
  | "phoneType"
  | "asc"
  | "lastVerified";

type SortBy = {
  option: SortingOption;
  desc: boolean;
};

const sortFns = {
  number: (a: PhoneContact, b: PhoneContact) => {
    return `${a.phoneNumber}${a.extension ?? ""}` >
      `${b.phoneNumber}${b.extension ?? ""}`
      ? 1
      : -1;
  },
  owner: (a: PhoneContact, b: PhoneContact) =>
    a.ownerType > b.ownerType ? 1 : -1,
  ownerName: (a: PhoneContact, b: PhoneContact) =>
    `${a.ownerFirstName} ${a.ownerLastName}`.toUpperCase() >
    `${b.ownerFirstName} ${b.ownerLastName}`.toUpperCase()
      ? 1
      : -1,
  phoneType: (a: PhoneContact, b: PhoneContact) =>
    a.phoneType > b.phoneType ? 1 : -1,
  asc: (a: PhoneContact, b: PhoneContact) =>
    (a.consentStatus ?? "") > (b.consentStatus ?? "") ? 1 : -1,
  lastVerified: (a: PhoneContact, b: PhoneContact) => {
    const sortResult =
      (a.lastVerifiedDate?.getTime() ?? 0) -
      (b.lastVerifiedDate?.getTime() ?? 0);
    return sortResult === 0
      ? a.lastCapturedDate.getTime() - b.lastCapturedDate.getTime()
      : sortResult;
  },
};

type Props = {
  options: GetPhoneOptionsResponse;
  headerClassName?: string;
  headerText: string;
  headerContent?: ReactNode;
  data: PhoneContact[];
  selectedToVerify: string[];
  onClickVerify: (id: string) => void;
  onClickView: (phoneContact: PhoneContact) => void;
  onClickEdit: (phoneContact: PhoneContact) => void;
  emptyTableMessage: string;
  className?: string;
  showInactivationReason: boolean;
};

export default function PhoneNumberTable({
  options,
  headerClassName,
  headerText,
  headerContent,
  data,
  selectedToVerify,
  onClickVerify,
  onClickView,
  onClickEdit,
  emptyTableMessage,
  className,
  showInactivationReason,
}: Props) {
  const [sortBy, setSortBy] = useState<SortBy>({
    option: "lastVerified",
    desc: true,
  });

  const sortedData = useMemo((): PhoneContact[] => {
    return data.sort(
      (a, b) => sortFns[sortBy.option](a, b) * (sortBy.desc ? -1 : 1),
    );
  }, [data, sortBy]);

  const columns: TableColumn<PhoneContact>[] = [
    {
      header: renderTableHeaderCell(
        strings.number,
        () =>
          setSortBy((prev) => {
            return {
              option: "number",
              desc: prev.option !== "number" ? false : !prev.desc,
            };
          }),
        sortBy.option === "number" ? sortBy.desc : undefined,
      ),
      size: 3,
      alignment: "left",
      render: (row) => (
        <div className={styles.phoneNumberContainer}>
          <div className={styles.statusBadge}>
            <span>{maskInput(INPUT_MASKS.phoneNumber, row.phoneNumber)}</span>
            <div className={styles.badgeAndRestrictionContainer}>
              <div className={styles.badgeRow}>
                {row.isPrimary ? (
                  <div className={styles.primary}>{strings.primary}</div>
                ) : null}
                {row.isSecondary ? (
                  <div className={styles.secondary}>{strings.secondary}</div>
                ) : null}
              </div>
              {row.restriction !== null && (
                <div className={styles.restricted}>
                  <span>{strings.restricted}</span>
                  <span className={styles.restrictedTooltipContent}>
                    {strings.restrictedBadge}
                  </span>
                </div>
              )}
            </div>
          </div>
          {row.extension ? (
            <span>{strings.extension(row.extension)}</span>
          ) : null}
        </div>
      ),
    },
    {
      header: renderTableHeaderCell(
        strings.owner,
        () =>
          setSortBy((prev) => {
            return {
              option: "owner",
              desc: prev.option !== "owner" ? false : !prev.desc,
            };
          }),
        sortBy.option === "owner" ? sortBy.desc : undefined,
      ),
      size: 2.5,
      alignment: "left",
      render: (row) => (
        <>
          {options.phoneOwnerTypes.find((o) => o.id === row.ownerType)?.value}
        </>
      ),
    },
    {
      header: renderTableHeaderCell(
        strings.cellOwnerName,
        () =>
          setSortBy((prev) => {
            return {
              option: "ownerName",
              desc: prev.option !== "ownerName" ? false : !prev.desc,
            };
          }),
        sortBy.option === "ownerName" ? sortBy.desc : undefined,
      ),
      size: 2.5,
      alignment: "left",
      render: (row) => (
        <>{`${row.ownerFirstName} ${row.ownerLastName} ${options.nameSuffixes.find((o) => o.id === row.ownerSuffix)?.value ?? ""}`}</>
      ),
    },
    {
      header: renderTableHeaderCell(
        strings.type,
        () =>
          setSortBy((prev) => {
            return {
              option: "phoneType",
              desc: prev.option !== "phoneType" ? false : !prev.desc,
            };
          }),
        sortBy.option === "phoneType" ? sortBy.desc : undefined,
      ),
      size: 1,
      alignment: "left",
      render: (row) => (
        <>{options.phoneTypes.find((o) => o.id === row.phoneType)?.value}</>
      ),
    },
    {
      header: renderTableHeaderCell(
        strings.asc,
        () =>
          setSortBy((prev) => {
            return {
              option: "asc",
              desc: prev.option !== "asc" ? false : !prev.desc,
            };
          }),
        sortBy.option === "asc" ? sortBy.desc : undefined,
      ),
      size: 1,
      alignment: "left",
      render: (row) => renderAscBadge(row.consentStatus),
    },
    {
      header: renderTableHeaderCell(
        strings.lastVerified,
        () =>
          setSortBy((prev) => {
            return {
              option: "lastVerified",
              desc: prev.option !== "lastVerified" ? true : !prev.desc,
            };
          }),
        sortBy.option === "lastVerified" ? sortBy.desc : undefined,
      ),
      size: 1.5,
      alignment: "left",
      render: (row) => (
        <>
          {row.lastVerifiedDate &&
            formatInCentralTime(row.lastVerifiedDate, "MM/dd/yyyy")}
        </>
      ),
    },
    {
      header: strings.lastCaptured,
      size: 1.5,
      alignment: "left",
      render: (row) => (
        <>{formatInCentralTime(row.lastCapturedDate, "MM/dd/yyyy")}</>
      ),
    },
    {
      header: showInactivationReason ? strings.reason : strings.source,
      size: 2,
      alignment: "left",
      render: (row) => (
        <>
          {showInactivationReason
            ? options.inactivationReasons.find(
                (o) => o.id === row.inactivation?.inactivationReasonId,
              )?.value
            : options.phoneSources.find(
                (o) =>
                  o.id ===
                  row.sourceType.sort((a, b) => b.count - a.count)[0].source,
              )?.value}
        </>
      ),
    },
    {
      header: strings.edit,
      size: 1,
      alignment: "left",
      render: (row) => (
        <TextButton
          className={styles.textButton}
          onClick={() => onClickEdit(row)}
        >
          {strings.edit}
        </TextButton>
      ),
    },
    {
      header: strings.view,
      size: 1,
      alignment: "left",
      render: (row) => (
        <TextButton
          className={styles.textButton}
          onClick={() => onClickView(row)}
        >
          {strings.view}
        </TextButton>
      ),
    },
    {
      header: strings.verify,
      size: 0.5,
      alignment: "left",
      render: (row) => (
        <StyledCheckbox
          aria-label={strings.verify}
          onChange={() => onClickVerify(row.phoneID)}
          checked={selectedToVerify.includes(row.phoneID)}
        />
      ),
    },
  ];

  return (
    <div className={className}>
      <div className={classNames(styles.tableHeader, headerClassName)}>
        <span>{headerText}</span>
        {headerContent}
      </div>
      <Table
        columns={columns}
        data={sortedData}
        className={styles.table}
        emptyTableMessage={emptyTableMessage}
      />
    </div>
  );
}
